// The same database as `db.ts`, read without Dexie.
//
// This exists for exactly one reason: the entry chunk must not carry Dexie. The
// app's first paint waits on two settings — the language and whether a phone was
// ever verified — and the honest place to start that read is `main.ts`, the 1.5 KB
// file the browser already has, so the disk read overlaps the download of the
// 100 KB app chunk instead of following it. Importing Dexie there would put 94 KB
// in front of the very thing we are trying to reach sooner.
//
// It reads what `db.ts` writes and owns no schema of its own. Dexie remains the
// only writer of structure: **this module never creates or upgrades anything.**

/// Must match `AppDb`'s constructor argument in db.ts.
const DB_NAME = 'dialano';

/// Store name and key path from the `settings: 'key'` line in db.ts.
const SETTINGS_STORE = 'settings';

interface SettingRowLike {
  key: string;
  value: unknown;
}

/**
 * Opens the existing database and nothing else.
 *
 * Deliberately versionless. A versioned open would announce an upgrade, and an
 * upgrade blocks while any other client holds the database — the service worker
 * during a push, another tab — which is one of the stalls this whole change set
 * exists to remove.
 *
 * The subtle part is a FIRST run, where there is no database yet. A versionless
 * open of a missing database still creates it, empty, at version 1 — and Dexie
 * would then find version 1 already current and skip creating its stores,
 * leaving a permanently broken install. So a fired `upgradeneeded` is the signal
 * that we just invented the database, and aborting that transaction unwinds the
 * creation: a versionchange transaction that aborts while establishing a new
 * database leaves no database behind. Deleting it explicitly would be worse than
 * redundant — the delete outlives this call and can land on the database Dexie
 * legitimately creates a moment later.
 */
function openExisting(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME);
    } catch {
      // Storage blocked outright (some private modes) — the caller falls back.
      resolve(undefined);
      return;
    }

    let creating = false;
    request.onupgradeneeded = () => {
      creating = true;
      request.transaction?.abort();
    };
    request.onerror = () => resolve(undefined);
    request.onblocked = () => resolve(undefined);
    request.onsuccess = () => {
      const database = request.result;
      if (creating || !database.objectStoreNames.contains(SETTINGS_STORE)) {
        // Either we just invented it, or it predates the store — not ours to fix.
        database.close();
        resolve(undefined);
        return;
      }
      resolve(database);
    };
  });
}

/**
 * Reads several settings in ONE transaction.
 *
 * The keys that do not exist are simply absent from the returned map; a database
 * that is missing, unreadable or still being created returns an empty map, which
 * every caller must treat as "not set" rather than as an error. Losing a stored
 * preference here costs a re-render, never correctness — Dexie reads the same
 * rows again the moment the app bundle is up.
 */
export function readSettings(keys: readonly string[]): Promise<Map<string, unknown>> {
  return new Promise((resolve) => {
    const found = new Map<string, unknown>();
    void openExisting().then((database) => {
      if (database === undefined) {
        resolve(found);
        return;
      }
      let store: IDBObjectStore;
      try {
        store = database.transaction(SETTINGS_STORE, 'readonly').objectStore(SETTINGS_STORE);
      } catch {
        database.close();
        resolve(found);
        return;
      }

      let pending = keys.length;
      if (pending === 0) {
        database.close();
        resolve(found);
        return;
      }
      const settle = (): void => {
        if (--pending === 0) {
          database.close();
          resolve(found);
        }
      };
      for (const key of keys) {
        const request = store.get(key);
        request.onsuccess = () => {
          const row = request.result as SettingRowLike | undefined;
          if (row !== undefined) {
            found.set(key, row.value);
          }
          settle();
        };
        request.onerror = settle;
      }
    });
  });
}
